import axios from "axios";

const api = axios.create({
  baseURL: "/api",
});

// Inject JWT token into every request automatically
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("admin_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-logout on 401
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      const isAdminRoute = err.config?.url?.includes("/admin");
      const isVerify = err.config?.url?.includes("/auth/verify");
      if (isAdminRoute || isVerify) {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
        // Redirect to login if on admin page
        if (
          window.location.pathname.startsWith("/admin") &&
          !window.location.pathname.startsWith("/admin/login")
        ) {
          window.location.href = "/admin/login";
        }
      }
    }
    return Promise.reject(err);
  }
);

// ── Public ────────────────────────────────────────────────────────────────────
export const getProducts = (params) => api.get("/products", { params });

export const getProduct = (id) => api.get(`/products/${id}`);

export const getReviews = () => api.get("/reviews");

export const getBanners = () => api.get("/banners");

export const getVideo = () => api.get("/video");

export const createOrder = (data) => api.post("/orders", data);

export const getPublicCertificates = () => api.get("/certificates");

export const getCertificate = (id) => api.get(`/certificates/${id}`);

export const purchaseCertificate = (id, email) => api.post("/certificates/purchase", { id, email });

export const confirmCertificatePayment = (orderId) =>
  api.post("/certificates/confirm", { orderId });

// ── Auth ──────────────────────────────────────────────────────────────────────
export const authLogin = (credentials) => api.post("/auth/login", credentials);
export const authVerify = () => api.get("/auth/verify");
export const authChangePassword = (data) => api.put("/auth/password", data);

// ── Admin ─────────────────────────────────────────────────────────────────────
export const adminGetProducts = () => api.get("/admin/products");
export const adminCreateProduct = (formData) =>
  api.post("/admin/products", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const adminUpdateProduct = (id, formData) =>
  api.put(`/admin/products/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const adminDeleteProduct = (id) => api.delete(`/admin/products/${id}`);

export const adminGetReviews = () => api.get("/admin/reviews");
export const adminCreateReview = (formData) =>
  api.post("/admin/reviews", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const adminUpdateReview = (id, formData) =>
  api.put(`/admin/reviews/${id}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
export const adminDeleteReview = (id) => api.delete(`/admin/reviews/${id}`);

export const adminGetBanners = () => api.get("/admin/banners");
export const adminCreateBanner = (formData) =>
  api.post("/admin/banners", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const adminUpdateBanner = (id, formData) =>
  api.put(`/admin/banners/${id}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
export const adminDeleteBanner = (id) => api.delete(`/admin/banners/${id}`);

export const adminGetVideo = () => api.get("/admin/video");
export const adminSetVideo = (data) => api.post("/admin/video", data);

export const adminGetOrders = () => api.get("/admin/orders");
export const adminGetOrder = (id) => api.get(`/admin/orders/${id}`);
export const adminUpdateOrderStatus = (id, status) =>
  api.put(`/admin/orders/${id}/status`, { status });

// ── Categories ────────────────────────────────────────────────────────────────
export const getCategories = () => api.get("/categories");

export const adminGetCategories = () => api.get("/admin/categories");
export const adminCreateCategory = (formData) =>
  api.post("/admin/categories", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const adminUpdateCategory = (id, formData) =>
  api.put(`/admin/categories/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const adminDeleteCategory = (id) => api.delete(`/admin/categories/${id}`);

// ── Product Types ─────────────────────────────────────────────────────────────
export const getProductTypes = () => api.get("/product-types");

export const adminGetProductTypes = () => api.get("/admin/product-types");
export const adminCreateProductType = (data) => api.post("/admin/product-types", data);
export const adminUpdateProductType = (id, data) => api.put(`/admin/product-types/${id}`, data);
export const adminDeleteProductType = (id) => api.delete(`/admin/product-types/${id}`);

export const validateCertificate = (code) => api.post("/certificates/validate", { code });

// ── Gift Certificates (admin) ─────────────────────────────────────────────────
export const adminGetCertificates = () => api.get("/admin/certificates");
export const adminCreateCertificate = (data) => api.post("/admin/certificates", data);
export const adminUpdateCertificate = (id, data) => api.put(`/admin/certificates/${id}`, data);
export const adminDeleteCertificate = (id) => api.delete(`/admin/certificates/${id}`);

export const getContacts = async () => {
  const res = await api.get("/admin/contacts");
  return res.data;
};

export const markContactAsRead = async (id) => {
  const res = await api.patch(`/admin/contacts/${id}/read`);
  return res.data;
};

export const generateCertificateByItem = (itemId) =>
  api.post(`/admin/order-items/${itemId}/generate-certificate`);

export async function searchCities(provider, query) {
  const res = await api.post("/shipping/cities", { provider, query });
  return res.data;
}

export async function getBranches(provider, cityRef) {
  const res = await api.post("/shipping/branches", { provider, cityRef });
  return res.data;
}

export default api;
